import type { Product, StockStatus } from '../../api/products';
import { cn } from '../../lib/utils';
import { fmtDayYear, fmtDaysAgo } from '../../utils/format';
import { Tooltip } from '../ui/tooltip';
import { DEALER_TOOLTIP } from './homeValueColumns';

/**
 * All items' vendor stock cells (BOT-505), on the Home Value conventions:
 * `0` is a confirmed zero, `—` is "no readable number", and every reading says
 * when it was taken. Readings older than 30 days stay visible but muted.
 */

export const STOCK_STATUS_LABELS: Record<StockStatus, string> = {
  in_stock: 'In stock',
  out_of_stock: 'Out of stock',
  direct_ship: 'Direct ship',
  no_pdc_section: 'No PDC stock',
};

const STATUS_NOTE: Record<StockStatus, string> = {
  direct_ship: 'Paccar shows NOF at every warehouse — ships direct from the supplier',
  no_pdc_section: 'Freightliner lists no warehouse stock for this part',
  in_stock: 'International shows a status, not a number',
  out_of_stock: 'International shows a status, not a number',
};

const NO_SOURCE = 'No stock source for this vendor yet';
const NO_READING = 'No reading yet';
const NO_NUMBER = 'The vendor’s page gave no readable number (not the same as zero)';

function readLine(p: Product): string | null {
  if (!p.stock_observed_day) return null;
  return `Read ${fmtDayYear(p.stock_observed_day)} (${fmtDaysAgo(p.stock_days_ago ?? 0)})`;
}

function vendorStockTooltip(p: Product): string {
  const read = readLine(p);
  if (p.vendor_stock != null) return read ?? NO_READING;
  if (p.stock_status) return [STATUS_NOTE[p.stock_status], read].filter(Boolean).join(' · ');
  if (!p.stock_has_source) return NO_SOURCE;
  if (!read) return NO_READING;
  return `${NO_NUMBER} · ${read}`;
}

export function VendorStockCell({ product: p }: { product: Product }) {
  const text = p.vendor_stock != null ? String(p.vendor_stock)
    : p.stock_status ? STOCK_STATUS_LABELS[p.stock_status]
    : null;
  return (
    <Tooltip label={vendorStockTooltip(p)}>
      {text == null ? (
        <span aria-label="Vendor stock" className="text-zinc-300">—</span>
      ) : (
        <span
          aria-label="Vendor stock"
          data-stale={p.stock_stale ? 'true' : undefined}
          className={cn(
            'whitespace-nowrap',
            p.vendor_stock != null && 'tabular-nums',
            p.stock_stale ? 'text-zinc-400' : 'text-foreground',
          )}
        >
          {text}
        </span>
      )}
    </Tooltip>
  );
}

export function DealerStockCell({ product: p }: { product: Product }) {
  const meaning = DEALER_TOOLTIP[(p.vendor ?? '').trim()] ?? 'Dealer stock — never a PDC promise';
  const read = readLine(p);
  const label = !p.stock_has_source ? NO_SOURCE : read ? `${meaning} · ${read}` : meaning;
  return (
    <Tooltip label={label}>
      {p.dealer_stock == null ? (
        <span aria-label="Dealer stock" className="text-zinc-300">—</span>
      ) : (
        <span
          aria-label="Dealer stock"
          className={cn('tabular-nums', p.stock_stale ? 'text-zinc-400' : 'text-foreground')}
        >
          {p.dealer_stock}
        </span>
      )}
    </Tooltip>
  );
}
